import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, User, Mail, Phone, MessageCircle, Building2, Cpu, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  ERP_OPTIONS,
  BUYER_TEAM_SIZE_OPTIONS,
  getOrCreateDgennyLeadId,
  type BuyerTeamSize,
  type ErpOption,
} from '@/lib/leadOptions';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

interface UTMParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  gclid: string;
  fbclid: string;
}

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  construtora: string;
  erp: ErpOption | '';
  erpOutro: string;
  tamanhoTimeCompras: BuyerTeamSize | '';
}

interface LeadFormErrors {
  name?: string;
  email?: string;
  phone?: string;
  construtora?: string;
  erp?: string;
  erpOutro?: string;
  tamanhoTimeCompras?: string;
}

interface LeadCaptureFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  redirectToWhatsApp?: boolean;
  whatsAppMessage?: string;
}

const WHATSAPP_NUMBER = '5548988509968';
const DEFAULT_WHATSAPP_MESSAGE = 'Olá! Quero entender como a dgenny pode ajudar minha operação.';

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  redirectToWhatsApp = true,
  whatsAppMessage = DEFAULT_WHATSAPP_MESSAGE,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    construtora: '',
    erp: '',
    erpOutro: '',
    tamanhoTimeCompras: '',
  });
  const [utmParams, setUtmParams] = useState<UTMParams>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    gclid: '',
    fbclid: '',
  });
  const [errors, setErrors] = useState<LeadFormErrors>({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    setUtmParams({
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || '',
      gclid: urlParams.get('gclid') || '',
      fbclid: urlParams.get('fbclid') || '',
    });
  }, []);

  const validateForm = (): boolean => {
    const newErrors: LeadFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    } else if (!/^\(?[1-9]{2}\)?\s?(?:9\d{4}|\d{4})-?\d{4}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Telefone inválido';
    }

    if (!formData.construtora.trim()) {
      newErrors.construtora = 'Construtora é obrigatória';
    }

    if (!formData.erp) {
      newErrors.erp = 'Selecione uma opção';
    } else if (formData.erp === 'Outro' && !formData.erpOutro.trim()) {
      newErrors.erpOutro = 'Informe o nome do ERP';
    }

    if (!formData.tamanhoTimeCompras) {
      newErrors.tamanhoTimeCompras = 'Selecione uma opção';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name as keyof LeadFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange = (field: 'erp' | 'tamanhoTimeCompras', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const pushToDataLayer = (dgennyLeadId: string) => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'form_submitted',
      fields: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        construtora: formData.construtora,
        erp: formData.erp === 'Outro' ? formData.erpOutro : formData.erp,
        tamanhoTimeCompras: formData.tamanhoTimeCompras,
        dgennyLeadId,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        gclid: utmParams.gclid,
        fbclid: utmParams.fbclid,
      },
    });
  };

  const createLeadInCRM = async (dgennyLeadId: string) => {
    try {
      const response = await fetch('/api/create-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          construtora: formData.construtora,
          erp: formData.erp,
          erpOutro: formData.erpOutro,
          tamanhoTimeCompras: formData.tamanhoTimeCompras,
          dgennyLeadId,
          source: 'website_form',
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_content: utmParams.utm_content,
          utm_term: utmParams.utm_term,
          gclid: utmParams.gclid,
          fbclid: utmParams.fbclid,
          whatsAppMessage: whatsAppMessage,
          pageUrl: window.location.href,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Error creating lead in CRM:', data);
      } else {
        console.log('Lead created in CRM:', data);
      }
    } catch (error) {
      console.error('Error calling CRM API:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dgennyLeadId = getOrCreateDgennyLeadId();
      pushToDataLayer(dgennyLeadId);
      createLeadInCRM(dgennyLeadId);

      toast({
        title: 'Dados enviados com sucesso!',
        description: redirectToWhatsApp
          ? 'Você será redirecionado para o WhatsApp...'
          : 'Entraremos em contato em breve.',
      });

      onSuccess?.();

      if (redirectToWhatsApp) {
        const erpFinal = formData.erp === 'Outro' ? formData.erpOutro : formData.erp;
        const encodedMessage = encodeURIComponent(
          [
            whatsAppMessage,
            '',
            `Nome: ${formData.name}`,
            `E-mail: ${formData.email}`,
            `Telefone: ${formData.phone}`,
            `Construtora: ${formData.construtora}`,
            `ERP: ${erpFinal}`,
            `Time de compras: ${formData.tamanhoTimeCompras}`,
          ].join('\n')
        );
        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

        setTimeout(() => {
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
          onClose();
        }, 1000);
      } else {
        onClose();
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        construtora: '',
        erp: '',
        erpOutro: '',
        tamanhoTimeCompras: '',
      });
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      toast({
        title: 'Erro ao enviar',
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-brand-azul">
            Fale com a dgenny
          </DialogTitle>
          <DialogDescription className="text-brand-azul/70">
            Algumas perguntas rápidas — assim a gente já chega preparado.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-brand-azul/80">
              Nome completo
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-azul/40" />
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Seu nome"
                value={formData.name}
                onChange={handleInputChange}
                className={`pl-10 ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* E-mail */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-brand-azul/80">
              E-mail
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-azul/40" />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleInputChange}
                className={`pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* WhatsApp */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-brand-azul/80">
              WhatsApp
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-azul/40" />
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="(00) 00000-0000"
                value={formData.phone}
                onChange={handleInputChange}
                className={`pl-10 ${errors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Construtora */}
          <div className="space-y-2">
            <Label htmlFor="construtora" className="text-sm font-medium text-brand-azul/80">
              Construtora
            </Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-azul/40" />
              <Input
                id="construtora"
                name="construtora"
                type="text"
                placeholder="Nome da sua construtora"
                value={formData.construtora}
                onChange={handleInputChange}
                className={`pl-10 ${errors.construtora ? 'border-red-500 focus:ring-red-500' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            {errors.construtora && (
              <p className="text-sm text-red-500">{errors.construtora}</p>
            )}
          </div>

          {/* ERP */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-brand-azul/80 flex items-center gap-2">
              <Cpu className="h-4 w-4 text-brand-azul/40" />
              Qual ERP vocês utilizam hoje?
            </Label>
            <Select
              value={formData.erp}
              onValueChange={(v) => handleSelectChange('erp', v)}
              disabled={isSubmitting}
            >
              <SelectTrigger className={errors.erp ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {ERP_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.erp && <p className="text-sm text-red-500">{errors.erp}</p>}
            {formData.erp === 'Outro' && (
              <div className="pt-2">
                <Input
                  name="erpOutro"
                  placeholder='Qual? (ex: "SAP", "sistema próprio")'
                  value={formData.erpOutro}
                  onChange={handleInputChange}
                  className={errors.erpOutro ? 'border-red-500' : ''}
                  disabled={isSubmitting}
                />
                {errors.erpOutro && (
                  <p className="text-sm text-red-500 mt-1">{errors.erpOutro}</p>
                )}
              </div>
            )}
          </div>

          {/* Tamanho do time */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-brand-azul/80 flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-azul/40" />
              Quantos compradores fazem parte do seu time?
            </Label>
            <Select
              value={formData.tamanhoTimeCompras}
              onValueChange={(v) => handleSelectChange('tamanhoTimeCompras', v)}
              disabled={isSubmitting}
            >
              <SelectTrigger className={errors.tamanhoTimeCompras ? 'border-red-500' : ''}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {BUYER_TEAM_SIZE_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.tamanhoTimeCompras && (
              <p className="text-sm text-red-500">{errors.tamanhoTimeCompras}</p>
            )}
          </div>

          <input type="hidden" name="utm_source" value={utmParams.utm_source} />
          <input type="hidden" name="utm_medium" value={utmParams.utm_medium} />
          <input type="hidden" name="utm_campaign" value={utmParams.utm_campaign} />
          <input type="hidden" name="utm_content" value={utmParams.utm_content} />
          <input type="hidden" name="utm_term" value={utmParams.utm_term} />
          <input type="hidden" name="gclid" value={utmParams.gclid} />
          <input type="hidden" name="fbclid" value={utmParams.fbclid} />

          <Button
            type="submit"
            className="w-full bg-brand-roxo hover:bg-brand-roxo/90 text-white font-semibold rounded-full py-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <MessageCircle className="mr-2 h-4 w-4" />
                Continuar para WhatsApp
              </>
            )}
          </Button>

          <p className="text-xs text-center text-brand-azul/60 mt-4">
            Ao continuar, você concorda com nossa{' '}
            <a href="/politica-de-privacidade" className="text-brand-azul hover:underline" target="_blank">
              Política de Privacidade
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureForm;
